/**
 * Underlying Quote Indicator
 *
 * Plots the REAL SPX or SPY price (Schwab stream, 1m OHLCV bars) as hollow
 * candlesticks REBASED onto the ES price scale: candle values are
 * `quote x session T-1 anchor ratio`, the SAME per-day ratio that places
 * the exposure/OI levels (spx_to_es_price). The anchor error cancels
 * between candle and level, so a rebased candle touching a level means the
 * underlying is literally at that strike, regardless of intraday ratio
 * drift; the gap between these candles and the ES candles IS the drift.
 *
 * Ratio-linked axis: the series live on the ES 'right' scale (one zoom
 * scales everything together), while a per-segment custom priceFormat
 * divides back by the anchor so axis/crosshair labels read in NATIVE
 * SPY/SPX prices at the ES-linked position.
 *
 * Overnight there is deliberately nothing to draw: a synthetic quote
 * (ES/ratio) rebased back collapses exactly onto the ES candles, so the ES
 * candles are the overnight representation. Real candles resume at 9:30
 * and split from ES by the true drift.
 *
 * One series per trading day (no candles across overnight gaps), 1m bars
 * aggregated client-side to the chart timeframe on the same floor(t/tf)
 * buckets the ES server candles use. Time-based chart types only.
 *
 * Data: enable(segments)/update(segments) with segments =
 * [{date, bars, anchor, anchorEs}] (raw 1m bars {t,o,h,l,c,v}; anchor =
 * that session's T-1 ratio for this underlying, null = segment not
 * drawn; anchorEs = the anchor's ES T-1 close numerator, optional, only
 * consumed by gain-compressed overlays via overlayRebaseCoeffs).
 * Live updates via the unified SSE channel 'underlying-quotes' (payload
 * carries the forming 1m bar).
 */

import { CandlestickSeries } from 'lightweight-charts';
import { unifiedSSE } from '../hooks/useUnifiedSSE';
import { OVERLAY_STYLES, overlayRebaseCoeffs } from './underlyingOverlays';

const TF_SECONDS = {
  '1s': 1, '5s': 5, '15s': 15, '30s': 30,
  '1m': 60, '5m': 300, '15m': 900, '30m': 1800,
  '1h': 3600, '4h': 14400, '1d': 86400,
};

const TRANSPARENT = 'rgba(0,0,0,0)';

// Per-underlying styles come from the overlay registry (SSOT). Futures
// overlays (nq/rty/ym/zn) rebase by a raw ES/root T-1 close ratio: a pure
// visual alignment scalar with NO strike identity (relative divergence
// only), and they draw the full ~23h Globex session; SPX/SPY rebase by
// the converter session ratio (strike identity, RTH-only).
const STYLE = OVERLAY_STYLES;

export class UnderlyingQuoteIndicator {
  /**
   * @param {object} chart - The main lightweight-charts IChartApi instance
   * @param {string} underlying - 'spx' | 'spy'
   */
  constructor(chart, underlying) {
    this.chart = chart;
    this.underlying = underlying;
    this.enabled = false;

    this._segments = [];        // [{date, bars, anchor, anchorEs}] raw 1m bars {t,o,h,l,c,v}
    this._drawable = [];        // segments with a non-null anchor (series exist for these)
    this._seriesList = [];      // one CandlestickSeries per drawable segment
    this._seriesAnchors = [];   // anchor each series was created with (formatter closure)
    this._seriesAnchorEs = [];  // anchorEs ditto (rebase coeffs are closed over too)
    this._sseUnsubs = [];
    this._interval = 60;        // aggregation bucket in seconds (chart timeframe)
    this._formingBucket = null; // bucket start ts of the forming candle
    this._formingBars = new Map(); // minuteTs -> 1m bar inside the forming bucket
  }

  // ── Series management ───────────────────────────────────────────────────

  _seriesOptions(isLast, coeffs) {
    const style = STYLE[this.underlying] || STYLE.spx;
    return {
      // noAutoscale (e.g. VIX): stay on the shared scale for pinned
      // comparability but contribute nothing to its autoscale, so a
      // high-vol overlay can never squash the ES candles (extreme
      // excursions clip out of the viewport instead).
      ...(style.noAutoscale ? { autoscaleInfoProvider: () => null } : {}),
      // ES scale: rebased values share the level geometry and one zoom
      priceScaleId: 'right',
      // Hollow: transparent bodies, colored borders + wicks
      upColor: TRANSPARENT,
      downColor: TRANSPARENT,
      borderUpColor: style.up,
      borderDownColor: style.down,
      wickUpColor: style.up,
      wickDownColor: style.down,
      lastValueVisible: isLast,
      priceLineVisible: false,
      title: isLast ? style.title : '',
      // Ratio-linked axis: labels read in NATIVE prices at the ES
      // position (exact inverse of the rebase, incl. any gain compression)
      priceFormat: {
        type: 'custom',
        minMove: 0.01,
        formatter: (v) => ((v - coeffs.b) / coeffs.m).toFixed(style.decimals),
      },
    };
  }

  _removeAllSeries() {
    for (const s of this._seriesList) {
      try { this.chart.removeSeries(s); } catch (e) { /* may already be detached */ }
    }
    this._seriesList = [];
    this._seriesAnchors = [];
    this._seriesAnchorEs = [];
  }

  /** Rebase coefficients for one segment (displayed = m * price + b). */
  _coeffs(seg) {
    return overlayRebaseCoeffs(this.underlying, seg.anchor, seg.anchorEs ?? null);
  }

  // ── Aggregation (1m bars -> rebased chart-timeframe candles) ─────────────

  setTimeframe(tf) {
    const newInterval = TF_SECONDS[tf] || 60;
    if (newInterval !== this._interval) {
      this._interval = newInterval;
      if (this.enabled) this._applyData();
    }
  }

  /**
   * Fold raw 1m bars into rebased {time, open, high, low, close} candles on
   * the floor(t/interval) grid: the same buckets the ES server candles use.
   * The rebase is linear and monotonic (m > 0), so candle ordering holds.
   */
  _aggregate(bars, coeffs) {
    const interval = this._interval;
    const { m, b: off } = coeffs;
    const out = [];
    let current = null;
    for (const b of bars) {
      const bucket = Math.floor(b.t / interval) * interval;
      if (!current || current.time !== bucket) {
        if (current) out.push(current);
        current = {
          time: bucket,
          open: b.o * m + off, high: b.h * m + off,
          low: b.l * m + off, close: b.c * m + off,
        };
      } else {
        current.high = Math.max(current.high, b.h * m + off);
        current.low = Math.min(current.low, b.l * m + off);
        current.close = b.c * m + off;
      }
    }
    if (current) out.push(current);
    return out;
  }

  _applyData() {
    this._drawable = this._segments.filter(s => s && s.anchor != null && s.bars?.length > 0);
    const n = this._drawable.length;

    // Rebuild series when the count OR any anchor/anchorEs changed (the
    // rebase coefficients are closed over in each series' price formatter)
    const anchorsChanged = this._seriesAnchors.length !== n
      || this._drawable.some((s, i) => this._seriesAnchors[i] !== s.anchor
        || this._seriesAnchorEs[i] !== (s.anchorEs ?? null));
    if (anchorsChanged) {
      this._removeAllSeries();
      for (let i = 0; i < n; i++) {
        const series = this.chart.addSeries(
          CandlestickSeries, this._seriesOptions(i === n - 1, this._coeffs(this._drawable[i]))
        );
        this._seriesList.push(series);
        this._seriesAnchors.push(this._drawable[i].anchor);
        this._seriesAnchorEs.push(this._drawable[i].anchorEs ?? null);
      }
    }

    for (let i = 0; i < n; i++) {
      this._seriesList[i].setData(this._aggregate(this._drawable[i].bars, this._coeffs(this._drawable[i])));
    }

    this._seedFormingBucket();
  }

  /**
   * Seed the forming-candle minute map from the tail of the last segment so
   * SSE updates merge with the already-fetched minutes of the same bucket.
   */
  _seedFormingBucket() {
    this._formingBucket = null;
    this._formingBars = new Map();
    const lastSeg = this._segments[this._segments.length - 1];
    if (!lastSeg || !lastSeg.bars || lastSeg.bars.length === 0) return;
    const bars = lastSeg.bars;
    const bucket = Math.floor(bars[bars.length - 1].t / this._interval) * this._interval;
    this._formingBucket = bucket;
    for (let i = bars.length - 1; i >= 0 && bars[i].t >= bucket; i--) {
      this._formingBars.set(bars[i].t, bars[i]);
    }
  }

  _formingCandle(coeffs) {
    const { m, b: off } = coeffs;
    const minutes = [...this._formingBars.keys()].sort((a, b) => a - b);
    if (minutes.length === 0) return null;
    const first = this._formingBars.get(minutes[0]);
    const candle = {
      time: this._formingBucket,
      open: first.o * m + off, high: first.h * m + off,
      low: first.l * m + off, close: first.c * m + off,
    };
    for (let i = 1; i < minutes.length; i++) {
      const b = this._formingBars.get(minutes[i]);
      candle.high = Math.max(candle.high, b.h * m + off);
      candle.low = Math.min(candle.low, b.l * m + off);
      candle.close = b.c * m + off;
    }
    return candle;
  }

  // ── SSE connection ──────────────────────────────────────────────────────

  _connectSSE() {
    this._disconnectSSE();

    this._sseUnsubs.push(
      unifiedSSE.subscribe('underlying-quotes', 'underlying_quote', (data) => {
        try {
          const bar = data.bar;
          if (data.underlying !== this.underlying || !bar || bar.t == null) return;

          // Live updates draw on the LAST series, which must correspond to
          // the LAST segment (today); skip if today's anchor is unresolved
          const lastSeg = this._segments[this._segments.length - 1];
          const lastDrawable = this._drawable[this._drawable.length - 1];
          if (!lastSeg || lastDrawable !== lastSeg) return;
          const lastSeries = this._seriesList[this._seriesList.length - 1];
          if (!lastSeries) return;

          // Keep the raw 1m bar in the segment (replace the forming minute
          // in place) so timeframe re-aggregation stays correct
          const bars = lastSeg.bars;
          if (bars.length > 0 && bars[bars.length - 1].t === bar.t) {
            bars[bars.length - 1] = bar;
          } else {
            bars.push(bar);
            if (bars.length > 28800) bars.shift();
          }

          const bucket = Math.floor(bar.t / this._interval) * this._interval;
          if (bucket !== this._formingBucket) {
            this._formingBucket = bucket;
            this._formingBars = new Map();
          }
          this._formingBars.set(bar.t, bar);

          const candle = this._formingCandle(this._coeffs(lastSeg));
          if (candle) lastSeries.update(candle);
        } catch (e) {
          console.error('[UnderlyingQuoteIndicator] Update handler error:', e);
        }
      })
    );
  }

  _disconnectSSE() {
    if (this._sseUnsubs.length > 0) {
      this._sseUnsubs.forEach(unsub => unsub());
      this._sseUnsubs = [];
    }
  }

  // ── Public API ──────────────────────────────────────────────────────────

  /**
   * Quote bucket close for the chart bucket starting at `time`: the close
   * of the LAST 1m bar inside [time, time + interval), so the tooltip
   * compares it against the ES candle close of the SAME bucket. Returns
   * {price, anchor, anchorEs} (anchor null when the session ratio is
   * unresolved; anchorEs feeds the tooltip's rebase coeffs) or null when
   * the bucket holds no quote data (e.g. overnight).
   * @param {number} time - bucket start, UTC epoch seconds
   */
  getValueAt(time) {
    if (!this.enabled) return null;
    const windowEnd = time + this._interval;
    for (let i = this._segments.length - 1; i >= 0; i--) {
      const seg = this._segments[i];
      const bars = seg?.bars;
      if (!bars || !bars.length || bars[0].t >= windowEnd) continue;
      // Bars are sorted; binary search the latest bar with t < windowEnd
      let lo = 0, hi = bars.length - 1, best = null;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (bars[mid].t < windowEnd) { best = bars[mid]; lo = mid + 1; }
        else { hi = mid - 1; }
      }
      if (best && best.t >= time) {
        return { price: best.c, anchor: seg.anchor ?? null, anchorEs: seg.anchorEs ?? null };
      }
      return null; // bucket has no quote data (overnight / gap)
    }
    return null;
  }

  /**
   * Enable with per-day segments [{date, bars, anchor}].
   * opts.noLive skips the SSE subscription (docs MiniChart renders static
   * fixture segments; the live chart passes no opts and is unchanged).
   */
  enable(segments, opts = {}) {
    this.enabled = true;
    this._segments = segments || [];
    this._applyData();
    if (!opts.noLive) this._connectSSE();
  }

  /**
   * Replace data segments (date-range or anchor change).
   */
  update(segments) {
    if (!this.enabled) return;
    this._segments = segments || [];
    this._applyData();
  }

  disable() {
    if (!this.enabled) return;
    this._disconnectSSE();
    this._removeAllSeries();
    this._segments = [];
    this._drawable = [];
    this._formingBucket = null;
    this._formingBars = new Map();
    this.enabled = false;
  }

  isEnabled() {
    return this.enabled;
  }
}

export default UnderlyingQuoteIndicator;
